import posthog from "posthog-js";
import { supabase } from "@/lib/supabase";
import { initErrorTracking, shutdownErrorTracking } from "@/lib/error-tracking";

/**
 * GDPR / §25 TTDSG consent state for non-essential services.
 *
 * Essential storage (auth session, language preference, this consent record
 * itself) is strictly necessary and never gated. Everything else must stay
 * off until the visitor opts in via the cookie consent modal:
 *
 * - analytics      → PostHog (product analytics, ph_* cookies + localStorage)
 * - errorTracking  → Sentry (browser error reports incl. IP / sendDefaultPii)
 */
export interface ConsentChoices {
  analytics: boolean;
  errorTracking: boolean;
}

export interface ConsentRecord extends ConsentChoices {
  /** Bump CONSENT_VERSION when categories/services change → users are re-asked. */
  version: number;
  /** ISO timestamp of the choice — the Art. 7(1) accountability record. */
  decidedAt: string;
}

export const CONSENT_VERSION = 1;

const CONSENT_STORAGE_KEY = "coliviq.cookie-consent.v1";

/** Consent is re-requested after 12 months (EDPB good-practice ceiling). */
const CONSENT_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

type ConsentListener = (record: ConsentRecord | null) => void;

const listeners = new Set<ConsentListener>();

export function getStoredConsent(): ConsentRecord | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentRecord>;
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      parsed.version !== CONSENT_VERSION ||
      typeof parsed.analytics !== "boolean" ||
      typeof parsed.errorTracking !== "boolean" ||
      typeof parsed.decidedAt !== "string"
    ) {
      return null;
    }
    const decidedAtMs = Date.parse(parsed.decidedAt);
    if (
      Number.isNaN(decidedAtMs) ||
      Date.now() - decidedAtMs > CONSENT_MAX_AGE_MS
    ) {
      return null;
    }
    return parsed as ConsentRecord;
  } catch {
    return null;
  }
}

export function saveConsent(choices: ConsentChoices): ConsentRecord {
  const record: ConsentRecord = {
    ...choices,
    version: CONSENT_VERSION,
    decidedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Private mode / quota: consent still applies for this session.
  }
  logConsentReceipt(record);
  applyConsent(record);
  for (const listener of listeners) listener(record);
  return record;
}

/**
 * Server-side consent receipt (GDPR Art. 7(1) burden of proof): one
 * insert-only row in public.consent_events per explicit decision in the
 * modal. Fire-and-forget — a failed insert (offline, table not migrated
 * yet) must never block or break the consent flow itself; the localStorage
 * record stays the source of truth for what runs in this browser.
 */
function logConsentReceipt(record: ConsentRecord) {
  try {
    void supabase
      .from("consent_events")
      .insert({
        consent_version: record.version,
        analytics: record.analytics,
        error_tracking: record.errorTracking,
        ui_language: localStorage.getItem("language"),
        user_agent: navigator.userAgent,
      })
      .then(({ error }: { error: unknown }) => {
        if (error && import.meta.env.DEV) {
          console.warn("[consent] receipt insert failed:", error);
        }
      });
  } catch {
    // Never let receipt logging interfere with applying the consent.
  }
}

export function onConsentChange(listener: ConsentListener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Window event that reopens the consent modal in its settings view. */
export const OPEN_COOKIE_SETTINGS_EVENT = "coliviq:open-cookie-settings";

/**
 * Reopen the cookie consent modal (settings view) — wired to the footer's
 * "Cookie settings" link so withdrawing consent stays one click away
 * (GDPR Art. 7(3)).
 */
export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
}

/** Remove PostHog's own cookies/localStorage after consent is withdrawn. */
function purgePostHogStorage() {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith("ph_")) localStorage.removeItem(key);
    }
    for (const cookie of document.cookie.split(";")) {
      const name = cookie.split("=")[0]?.trim();
      if (name && name.startsWith("ph_")) {
        document.cookie = `${name}=; Max-Age=0; path=/`;
        document.cookie = `${name}=; Max-Age=0; path=/; domain=.${location.hostname}`;
      }
    }
  } catch {
    // Best-effort cleanup only.
  }
}

/**
 * Turn services on/off to match the given consent. Safe to call repeatedly;
 * runs at boot with the stored record and again on every saveConsent().
 */
export function applyConsent(record: ConsentRecord | null) {
  // PostHog is always init'd opted-out with in-memory persistence (see
  // main.tsx), so before/without consent nothing is captured or stored.
  if (posthog.__loaded) {
    if (record?.analytics) {
      posthog.set_config({ persistence: "localStorage+cookie" });
      posthog.opt_in_capturing();
    } else {
      posthog.opt_out_capturing();
      posthog.set_config({ persistence: "memory" });
      purgePostHogStorage();
    }
  }

  if (record?.errorTracking) {
    initErrorTracking();
  } else {
    shutdownErrorTracking();
  }
}
